import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../config";
import CompactYouTubePlayer from "../components/CompactYouTubePlayer";
import StoryCard from "../components/episode/StoryCard";
import useStoryEditing from "../hooks/useStoryEditing";
import { FaArrowLeft, FaPlus } from "react-icons/fa";

const ADMIN_PASSWORD = "kk26";
// Story card + autosave now live in components/episode/StoryCard.js and
// hooks/useStoryEditing.js (shared with the Episode Editor).

// ═══════════════════════════════════════════════════════════
// ─── Main Component ──────────────────────────────────────
// ═══════════════════════════════════════════════════════════

const EpisodeMetadataEditor = () => {
  const { episodeId } = useParams();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [episode, setEpisode] = useState(null);
  const [performances, setPerformances] = useState([]);
  const [stories, setStories] = useState({});
  const [writers, setWriters] = useState({}); // writer_id -> name_telugu
  const [storyReels, setStoryReels] = useState({}); // story_id -> latest row
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [currentTime, setCurrentTime] = useState(0);
  const playerRef = useRef(null);

  const [activeTab, setActiveTab] = useState(null);

  const { saveStatus, onSaveField, onGenerateReel, handleAddStory, registerReels } = useStoryEditing({
    episodeId, setStories, setPerformances, setWriters, setStoryReels,
  });

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) setIsAuthenticated(true);
    else setErrorMsg("Incorrect password.");
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setNotFound(false);
    try {
      const { data: ep, error: epErr } = await supabase.from("episodes").select("*").eq("episode_id", episodeId).single();
      if (epErr || !ep) { setNotFound(true); return; }
      setEpisode(ep);

      const { data: perfData } = await supabase
        .from("performances")
        .select("*")
        .eq("episode_id", episodeId)
        .not("story_id", "is", null)
        .order("story_start_sec", { ascending: true, nullsFirst: false });
      const perfs = perfData || [];
      setPerformances(perfs);

      const storyIds = [...new Set(perfs.map((p) => p.story_id).filter(Boolean))];
      if (storyIds.length > 0) {
        const { data: storyData } = await supabase.from("stories").select("*").in("story_id", storyIds);
        const map = {};
        (storyData || []).forEach((s) => { map[s.story_id] = s; });
        setStories(map);

        const writerIds = [...new Set((storyData || []).map((s) => s.writer_id).filter(Boolean))];
        if (writerIds.length > 0) {
          const { data: writerData } = await supabase.from("writers").select("writer_id,name_telugu").in("writer_id", writerIds);
          const wmap = {};
          (writerData || []).forEach((w) => { wmap[w.writer_id] = w.name_telugu; });
          setWriters(wmap);
        }

        const { data: reelData } = await supabase
          .from("story_reels")
          .select("*")
          .in("story_id", storyIds)
          .order("created_at", { ascending: false });
        const reelMap = {};
        (reelData || []).forEach((r) => {
          if (!reelMap[r.story_id]) reelMap[r.story_id] = r; // first hit = most recent (already ordered desc)
        });
        setStoryReels(reelMap);
        registerReels(reelMap);
      }
    } catch (err) {
      console.error(err);
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  }, [episodeId, registerReels]);

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [isAuthenticated, fetchData]);

  const onSeek = useCallback((sec) => playerRef.current?.playFrom(sec), []);

  // ─── Group performances by storyteller, one tab per storyteller ───
  const groups = useMemo(() => {
    const map = new Map();
    performances.forEach((p) => {
      const key = p.storyteller_id || p.storyteller_name_telugu || `perf_${p.performance_id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          telugu: p.storyteller_name_telugu || "",
          english: p.storyteller_name_english || "",
          storyteller_id: p.storyteller_id,
          perfs: [],
          minStart: p.story_start_sec ?? Infinity,
        });
      }
      const g = map.get(key);
      g.perfs.push(p);
      g.minStart = Math.min(g.minStart, p.story_start_sec ?? Infinity);
    });
    return [...map.values()].sort((a, b) => a.minStart - b.minStart);
  }, [performances]);

  useEffect(() => {
    if (groups.length > 0 && (activeTab == null || !groups.some((g) => g.key === activeTab))) {
      setActiveTab(groups[0].key);
    }
  }, [groups, activeTab]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-kk-ink flex items-center justify-center">
        <div className="bg-white/5 border border-kk-gold/20 rounded-xl p-8 max-w-md w-full">
          <h1 className="font-krushna text-kk-gold text-2xl mb-6 text-center">Episode Metadata Editor</h1>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full px-4 py-3 bg-white/5 border border-kk-gold/20 rounded-lg text-white mb-4
                focus:ring-2 focus:ring-kk-gold focus:border-transparent placeholder:text-white/30"
            />
            <button type="submit" className="w-full bg-kk-gold text-kk-ink py-3 rounded-lg hover:bg-kk-amber font-semibold transition-colors">
              Login
            </button>
            {errorMsg && <p className="text-red-400 text-sm mt-2">{errorMsg}</p>}
          </form>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-kk-ink flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-kk-gold/30 border-t-kk-gold rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-kk-ink flex flex-col items-center justify-center text-center px-6">
        <h1 className="text-2xl font-bold text-kk-cream mb-4">Episode Not Found</h1>
        <Link to="/episode-creator" className="px-6 py-2 bg-kk-gold text-kk-ink rounded-full font-semibold hover:bg-kk-amber">
          Back to Episodes
        </Link>
      </div>
    );
  }

  const videoId = episode?.video_id || episode?.youtube_broadcast_id;
  const activeGroup = groups.find((g) => g.key === activeTab);

  return (
    <div className="min-h-screen bg-kk-ink text-white">
      <div className="sticky top-0 z-30 bg-kk-ink border-b border-kk-gold/10">
        <div className="bg-kk-ink/95 border-b border-kk-gold/5 px-4 py-2">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <Link to="/episode-creator" className="flex items-center gap-2 text-kk-gold/70 hover:text-kk-gold text-sm transition-colors">
              <FaArrowLeft className="text-xs" /> Episodes
            </Link>
            <span className="text-white/20 text-xs font-mono">Ep {episode.episode_id} — Metadata Editor</span>
          </div>
        </div>
        {videoId && (
          <CompactYouTubePlayer ref={playerRef} videoId={videoId} onTimeUpdate={setCurrentTime} sticky={false} />
        )}
      </div>

      <div className="max-w-4xl mx-auto px-4 pt-4 pb-2">
        <h1 className="font-krushna text-kk-cream text-lg leading-tight mb-1">
          {episode.title || `Episode ${episode.episode_id}`}
        </h1>
        <p className="text-xs text-white/40">
          {groups.length} storyteller(s), {performances.length} story performance(s) — edits autosave a moment after you stop typing.
        </p>
      </div>

      {groups.length === 0 ? (
        <div className="text-center py-12 text-white/30">
          <p>No story performances (with a linked story_id) found for this episode.</p>
        </div>
      ) : (
        <>
          {/* ═══ TABS ═══ */}
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center gap-1 overflow-x-auto border-b border-white/10 pb-px">
              {groups.map((g) => {
                const selected = g.key === activeTab;
                return (
                  <button
                    key={g.key}
                    onClick={() => setActiveTab(g.key)}
                    className={`px-4 py-2.5 text-sm font-krushna whitespace-nowrap border-b-2 transition-colors
                      ${selected
                        ? "border-kk-gold text-kk-gold"
                        : "border-transparent text-white/50 hover:text-white/80"}`}
                  >
                    {g.telugu || g.english}
                    {g.perfs.length > 1 && (
                      <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-white/50">{g.perfs.length}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ═══ ACTIVE TAB CONTENT ═══ */}
          <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
            {activeGroup && activeGroup.perfs.map((perf) => (
              <StoryCard
                key={perf.performance_id}
                perf={perf}
                story={stories[perf.story_id]}
                writerName={writers[stories[perf.story_id]?.writer_id]}
                storyReel={storyReels[perf.story_id]}
                onSaveField={onSaveField}
                onGenerateReel={onGenerateReel}
                onSeek={onSeek}
                currentTime={currentTime}
                saveStatus={saveStatus}
              />
            ))}

            {activeGroup && (
              <button
                onClick={() => handleAddStory(activeGroup)}
                className="flex items-center gap-2 px-3 py-2.5 border border-dashed border-kk-gold/25 rounded-lg
                  text-kk-gold/60 text-sm hover:text-kk-gold hover:border-kk-gold/40 transition-colors w-full justify-center"
              >
                <FaPlus className="text-[10px]" /> Add another story for {activeGroup.telugu || activeGroup.english}
                <span className="text-[11px] text-white/30">(not auto-detected — e.g. told a 2nd story)</span>
              </button>
            )}
          </div>
        </>
      )}

      <div className="h-20" />
    </div>
  );
};

export default EpisodeMetadataEditor;
